import {
  getAdminDashboard, getUserContent, testGenerationSettings, updateGenerationSettings,
  updateCommerceSettings, updateAccessSettings, updateLandingShowcase, previewInvoice, upsertSchool, adminCreditWallet, adminCreditSchoolWallet,
  resolveMarketplaceComplaint, resolveSellerRecovery, upsertRecommender, setUnlimitedAccess, setSchoolMarketplaceSelling, setSchoolPinnedLabels,
  runEmbeddingBackfill, getEmbeddingBackfillStatus, runTopicOwnerBackfill, getTopicOwnerBackfillStatus,
  saveMarketplaceLabel, deleteMarketplaceLabel,
} from '../lib/admin.js';
import { navigate } from '../router.js';
import { formatPaise } from '../lib/wallet.js';
import { friendlyError } from '../lib/friendlyError.js';
import { currentUser, setAdminCached } from '../lib/auth.js';
import { confirmDialog } from '../lib/dialog.js';

const PROVIDERS = ['deepseek', 'openai', 'anthropic', 'gemini'];
const IMAGE_PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Gemini' },
];
// Image Recognition's own provider list — same two providers as
// IMAGE_PROVIDERS above right now, but kept separate since the two features
// validate independently server-side (generationSettingsValidation.js) and a
// future provider could apply to only one of them.
const IMAGE_RECOGNITION_PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Gemini' },
];
// Offline fallback only — the real options come live from OpenAI's
// /v1/models via functions/admin.js getImageModelCatalog (see
// normalizeImageCatalog below), so a new image model (e.g.
// gpt-image-1-mini) shows up here without a code change.
const FALLBACK_IMAGE_MODEL_CATALOG = {
  openai: [
    { id: 'gpt-image-2', label: 'GPT Image 2 — highest quality' },
    { id: 'gpt-image-1.5', label: 'GPT Image 1.5' },
  ],
  gemini: [
    { id: 'gemini-3-pro-image', label: 'Gemini 3 Pro Image — highest quality' },
    { id: 'gemini-3-flash-image', label: 'Gemini 3 Flash Image — cheap/fast' },
  ],
};
const FALLBACK_MODEL_CATALOG = {
  openai: [
    { id: 'gpt-5.5', label: 'GPT-5.5', reasoningLevels: ['low', 'medium', 'high'] },
    { id: 'gpt-5.4', label: 'GPT-5.4', reasoningLevels: ['low', 'medium', 'high'] },
    { id: 'gpt-5.4-mini', label: 'GPT-5.4 Mini', reasoningLevels: ['low', 'medium', 'high'] },
  ],
  deepseek: [
    { id: 'deepseek-v4-pro', label: 'DeepSeek v4 Pro', reasoningLevels: [''] },
    { id: 'deepseek-v4-flash', label: 'DeepSeek v4 Flash', reasoningLevels: [''] },
  ],
  anthropic: [
    { id: 'claude-opus-5', label: 'Claude Opus 5', reasoningLevels: ['low', 'medium', 'high'] },
    { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', reasoningLevels: ['low', 'medium', 'high'] },
    { id: 'claude-fable-5', label: 'Claude Fable 5', reasoningLevels: ['low', 'medium', 'high'] },
    { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', reasoningLevels: ['low', 'medium'] },
  ],
  gemini: [
    { id: 'gemini-3-pro', label: 'Gemini 3 Pro', reasoningLevels: ['low', 'medium', 'high'] },
    { id: 'gemini-3-flash', label: 'Gemini 3 Flash', reasoningLevels: ['low', 'medium', 'high'] },
  ],
};

export async function renderAdmin(container) {
  container.innerHTML = `
    <main class="main-full admin-page">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;">
        <div>
          <h1 style="margin:0;">Admin Dashboard</h1>
          <p style="margin:6px 0 0;color:var(--text-dim);">Global model routing and basic platform activity.</p>
        </div>
        <div id="admin-header-actions" style="display:flex;gap:8px;flex-wrap:wrap;">
          <button class="btn btn-sm" id="admin-back-btn">Back home</button>
        </div>
      </div>
      <div id="admin-body" class="settings-grid">
        <div class="settings-panel">Loading admin dashboard...</div>
      </div>
    </main>
  `;

  container.querySelector('#admin-back-btn').addEventListener('click', () => navigate('/'));
  const body = container.querySelector('#admin-body');

  let data;
  try {
    data = await getAdminDashboard();
    // Keep the per-uid Admin-tab flag in sync with the server's verdict
    // (see lib/auth.js) — same pattern as the school dashboard.
    setAdminCached(currentUser()?.uid, true);
  } catch (err) {
    setAdminCached(currentUser()?.uid, false);
    body.innerHTML = `
      <section class="settings-panel">
        <h3>Access unavailable</h3>
        <p style="color:var(--text-dim);line-height:1.5;">${escapeHtml(friendlyError(err, 'This account is not configured as an administrator.'))}</p>
        <p style="color:var(--text-dim);line-height:1.5;">In Firestore, set <code>appConfig/admin</code> with <code>adminEmails: ["your@email.com"]</code>.</p>
      </section>
    `;
    return;
  }

  // This control is created only after the server has verified that the
  // signed-in account is an administrator.
  const headerActions = container.querySelector('#admin-header-actions');
  const testAreaButton = document.createElement('button');
  testAreaButton.className = 'btn btn-primary btn-sm';
  testAreaButton.textContent = 'Dynamic experiment test area';
  testAreaButton.addEventListener('click', () => navigate('/dwar/test-area'));
  headerActions.prepend(testAreaButton);

  const uiReplicaButton = document.createElement('button');
  uiReplicaButton.className = 'btn btn-primary btn-sm';
  uiReplicaButton.textContent = 'UI Replica Studio';
  uiReplicaButton.addEventListener('click', () => navigate('/dwar/ui-replica'));
  headerActions.prepend(uiReplicaButton);

  const agentActivityButton = document.createElement('button');
  agentActivityButton.className = 'btn btn-primary btn-sm';
  agentActivityButton.textContent = 'Agent Activity';
  agentActivityButton.addEventListener('click', () => navigate('/dwar/agent-activity'));
  headerActions.prepend(agentActivityButton);

  const kinematicsLabButton = document.createElement('button');
  kinematicsLabButton.className = 'btn btn-primary btn-sm';
  kinematicsLabButton.textContent = 'Kinematics Testbed Lab';
  kinematicsLabButton.addEventListener('click', () => navigate('/dwar/kinematics-lab'));
  headerActions.prepend(kinematicsLabButton);

  const colliderLabButton = document.createElement('button');
  colliderLabButton.className = 'btn btn-primary btn-sm';
  colliderLabButton.textContent = 'LHC Collider Lab';
  colliderLabButton.addEventListener('click', () => navigate('/dwar/collider-lab'));
  headerActions.prepend(colliderLabButton);

  renderDashboard(body, data);
}

function renderDashboard(body, data) {
  const s = data.settings || {};
  const stats = data.stats || {};
  const catalog = normalizeCatalog(data.modelCatalog);
  const imageCatalog = normalizeImageCatalog(data.imageModelCatalog);
  const c = {
    ...(data.commerce?.settings || {}),
    schools: data.commerce?.schools || [],
    recommenders: data.commerce?.recommenders || [],
  };
  const a = data.access || {};
  body.innerHTML = `
    <section class="settings-panel" style="grid-column:1/-1;display:flex;gap:24px;flex-wrap:wrap;align-items:center;">
      <div>
        <p style="margin:0;color:var(--text-dim);font-size:0.8rem;text-transform:uppercase;letter-spacing:0.04em;">Average API calls per successful content generation</p>
        <p style="margin:4px 0 0;font-size:2.2rem;font-weight:800;line-height:1;">${(stats.avgApiCallsPerGeneration || 0).toFixed(2)}</p>
        <p style="margin:6px 0 0;color:var(--text-dim);font-size:0.8rem;">Across ${formatNumber(stats.successfulGenerations)} successful generations · ${formatNumber(stats.totalGenerationApiCalls)} total AI-provider calls (generation + continuations/retries + routing + moderation + fact-check + image assets).</p>
      </div>
      <div>
        <p style="margin:0;color:var(--text-dim);font-size:0.8rem;text-transform:uppercase;letter-spacing:0.04em;">Average time per generation</p>
        <p style="margin:4px 0 0;font-size:2.2rem;font-weight:800;line-height:1;">${formatDuration(stats.avgGenerationSeconds)}</p>
        <p style="margin:6px 0 0;color:var(--text-dim);font-size:0.8rem;">Wall-clock time from request start to a delivered page, averaged across the same ${formatNumber(stats.successfulGenerations)} successful generations. This is the same number shown to learners as an expected wait.</p>
      </div>
    </section>

    <section class="settings-panel admin-test-area-entry" style="grid-column:1/-1;">
      <div>
        <span class="pill">Private · administrator only</span>
        <h3 style="margin-top:10px;">Dynamic Experiment Test Area</h3>
        <p style="color:var(--text-dim);line-height:1.5;margin-bottom:0;">A secluded workbench for testing interactive cause-and-effect experiments before anything is considered for the learning experience.</p>
      </div>
      <button class="btn btn-primary" id="open-admin-test-area">Open test area</button>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <!-- Book import is DELINKED — dead code as of the "no live sessions"
           change (see functions/index.js's comment where startBookImport used
           to be exported). The panel that lived here was removed; the client
           lib (src/lib/bookImport.js) and backend (functions/bookImport.js)
           remain in the tree for reference only. -->
      <p style="color:var(--text-dim);line-height:1.5;margin-bottom:0;">📕 Book import was removed — it depended on the session-resume path that no longer exists.</p>
    </section>


    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Content search embedding backfill</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Topics/listings already generated before semantic search shipped have no search embedding yet — new content gets one automatically. Run this once to backfill everything existing (safe to rerun; already-embedded content is skipped).</p>
      <button class="btn btn-primary" id="embedding-backfill-start">Run backfill</button>
      <p id="embedding-backfill-status" style="color:var(--text-dim);margin:10px 0 0;font-size:0.88rem;"></p>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Topic name search backfill</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Topics created before cross-course "search all my topics" shipped have no <code>ownerUid</code> field yet, so they won't show up in that search until this runs once. New topics get it automatically. Safe to rerun.</p>
      <button class="btn btn-primary" id="topic-owner-backfill-start">Run backfill</button>
      <p id="topic-owner-backfill-status" style="color:var(--text-dim);margin:10px 0 0;font-size:0.88rem;"></p>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Generation Settings</h3>
      <p style="color:var(--text-dim);line-height:1.5;">These settings apply to all users. Every AI-backed feature on the platform gets its own model choice below, named for the feature it actually powers — each card tells you <strong>when it fires</strong> first, then the reasoning behind its default.</p>
      ${settingsProvenanceLine(data.settingsProvenance)}

      <p style="font-weight:700;margin:16px 0 2px;">Standard Content <span style="font-weight:400;color:var(--text-dim);">— topic-title suggestions &amp; kit-gap diagnostics</span> <span class="pill" style="background:var(--accent-soft);color:var(--accent);">🧠 Thinking model</span></p>
      <p style="margin:0 0 10px;font-size:0.88rem;"><strong>Fires when:</strong> a creator gets a topic-title suggestion (suggestHandler.js), or an admin re-runs a Kit-Gap Analysis theme (kitGapReplay.js, admin-only diagnostic). <strong>No longer used for actual page generation</strong> — the coding agent below (Coding Agent card) writes every page now, on its own model, regardless of subject matter.</p>
      <p style="color:var(--text-dim);font-size:0.82rem;margin:0 0 10px;">These are short, cheap calls compared to a real page build.</p>
      <div class="field-row">
        ${selectField('Provider', 'defaultProvider', PROVIDERS, s.defaultProvider)}
        ${selectField('Model', 'defaultModel', modelOptionsFor(catalog, s.defaultProvider), s.defaultModel)}
      </div>
      <div class="field-row" style="margin-top:12px;">
        ${selectField('Reasoning', 'defaultReasoningEffort', reasoningOptionsFor(catalog, s.defaultProvider, s.defaultModel), s.defaultReasoningEffort || '')}
      </div>
      <hr style="border:none;border-top:1px solid var(--border);margin:18px 0;">

      <p style="font-weight:700;margin:0 0 2px;">Coding Agent <span style="font-weight:400;color:var(--text-dim);">— the model that actually writes/edits every page</span> <span class="pill">⚡ Non-thinking model</span></p>
      <p style="margin:0 0 10px;font-size:0.88rem;"><strong>Fires when:</strong> literally every generation — this is functions/htmlAgentHandler.js's main tool-calling loop (write_file/edit_document/check_page/finish), the one thing every topic build runs through now, whole-page or section-targeted, simulation or not. It runs ONE OR MANY rounds per build (a real build can be several rounds of write-then-check), so this is the single highest-volume model call on the whole platform.</p>
      <p style="color:var(--text-dim);font-size:0.82rem;margin:0 0 10px;">Deliberately the <strong>cheap, non-thinking</strong> tier: each round is a narrow "implement this piece of the change" task, so a fast model handles it about as well as a thinking one, at a fraction of the cost, across the many rounds a real build makes. A thinking model here would just spend extra time and cost with nothing new to reason about.</p>
      <div class="field-row">
        ${selectField('Provider', 'agentModelProvider', PROVIDERS, s.agentModelProvider)}
        ${selectField('Model', 'agentModel', modelOptionsFor(catalog, s.agentModelProvider), s.agentModel)}
      </div>
      <div class="field-row" style="margin-top:12px;">
        ${selectField('Reasoning', 'agentModelReasoningEffort', reasoningOptionsFor(catalog, s.agentModelProvider, s.agentModel), s.agentModelReasoningEffort || '')}
      </div>
      <hr style="border:none;border-top:1px solid var(--border);margin:18px 0;">

      <p style="font-weight:700;margin:0 0 2px;">Verification <span style="font-weight:400;color:var(--text-dim);">— completeness finish-gate &amp; post-generation fidelity check</span> <span class="pill">⚡ Non-thinking model</span></p>
      <p style="margin:0 0 10px;font-size:0.88rem;"><strong>Fires when:</strong> the post-generation fidelity check (functions/fidelityCheck.js), which derives its own checklist from the instruction and runs once the build is done, comparing the page against it. Fail-open (an outage here never blocks a page from shipping) and feeds the "Render probe" / "Silent observers" section of the Agent Activity panel, not a blocking gate an admin can see fail.</p>
      <p style="color:var(--text-dim);font-size:0.82rem;margin:0 0 10px;">A judgment/comparison task against text already in hand, not open-ended reasoning — a fast, non-thinking model is enough, which is why this defaults to the same cheap tier as Dynamic Content below. htmlAgentRunner.js's ensureDistinctVerificationModel forces this off the Coding Agent's own model/provider if an admin ever points both slots at the same one — a same-model check tends to agree with its own work.</p>
      <div class="field-row">
        ${selectField('Provider', 'verificationProvider', PROVIDERS, s.verificationProvider)}
        ${selectField('Model', 'verificationModel', modelOptionsFor(catalog, s.verificationProvider), s.verificationModel)}
      </div>
      <div class="field-row" style="margin-top:12px;">
        ${selectField('Reasoning', 'verificationReasoningEffort', reasoningOptionsFor(catalog, s.verificationProvider, s.verificationModel), s.verificationReasoningEffort || '')}
      </div>
      <p style="color:var(--text-dim);font-size:0.78rem;margin:12px 0 0;">The old opt-in "fact-check repair pass" toggle that used to live here is gone from this panel — functions/factCheck.js is no longer required anywhere in the active pipeline (it was part of generateHandler.js/generationGraph.js, retired when the coding agent replaced them), so the toggle did nothing regardless of its setting.</p>
      <hr style="border:none;border-top:1px solid var(--border);margin:18px 0;">

      <p style="font-weight:700;margin:0 0 2px;">Dynamic Content <span style="font-weight:400;color:var(--text-dim);">— in-page "Ask AI" bridge</span> <span class="pill">⚡ Non-thinking model</span></p>
      <p style="margin:0 0 10px;font-size:0.88rem;"><strong>Fires when:</strong> a learner interacts with <code>window.askAI()</code> inside an already-generated page — a fresh practice problem, a quick Q&amp;A, or a check on a learner's answer.</p>
      <p style="color:var(--text-dim);font-size:0.82rem;margin:0 0 10px;">Kept independent of Standard Content above on purpose: this is meant to feel instant and cheap, not full-page quality — pick a fast, non-thinking model. Worth knowing: DeepSeek's own models default to thinking <strong>on</strong> (high effort) unless a request explicitly turns it off, and on a small reply budget like this one, that used to let the model silently burn its whole budget on invisible reasoning and return nothing — the platform now explicitly disables thinking for this tier regardless of which DeepSeek model is picked here.</p>
      <div class="field-row">
        ${selectField('Provider', 'askAiProvider', PROVIDERS, s.askAiProvider)}
        ${selectField('Model', 'askAiModel', modelOptionsFor(catalog, s.askAiProvider), s.askAiModel)}
      </div>
      <div class="field-row" style="margin-top:12px;">
        ${selectField('Reasoning', 'askAiReasoningEffort', reasoningOptionsFor(catalog, s.askAiProvider, s.askAiModel), s.askAiReasoningEffort || '')}
      </div>
      <hr style="border:none;border-top:1px solid var(--border);margin:18px 0;">

      <p style="font-weight:700;margin:0 0 2px;">Image Generation <span class="pill">🎨 Not applicable</span></p>
      <p style="margin:0 0 10px;font-size:0.88rem;"><strong>Fires when:</strong> a creator explicitly requests a new image or illustration be created. Never runs on its own.</p>
      <p style="color:var(--text-dim);font-size:0.82rem;margin:0 0 10px;">Image models don't have a thinking/non-thinking mode the way chat/text models above do — there's no reasoning-effort choice here at all.</p>
      <div class="field-row">
        ${selectField('Provider', 'imageProvider', IMAGE_PROVIDERS, s.imageProvider || 'openai')}
        ${selectField('Model', 'imageModel', modelOptionsFor(imageCatalog, s.imageProvider || 'openai'), s.imageModel || 'gpt-image-2')}
      </div>
      <div class="field-row" style="margin-top:12px;">
        ${selectField('Quality (enforced)', 'imageQuality', [{ value: 'high', label: 'High — publication grade' }], 'high')}
        ${selectField('Size', 'imageSize', ['1536x1024', '1024x1024', '1024x1536'], s.imageSize || '1536x1024')}
      </div>
      <hr style="border:none;border-top:1px solid var(--border);margin:18px 0;">

      <p style="font-weight:700;margin:0 0 2px;">Image Recognition <span style="font-weight:400;color:var(--text-dim);">— UI Replica Studio (admin-only)</span> <span class="pill">⚡🧠 Mixed</span></p>
      <p style="margin:0 0 10px;font-size:0.88rem;"><strong>Fires when:</strong> an admin uploads a reference image in UI Replica Studio — classifies it, then builds a 2D/3D replica from it.</p>
      <p style="color:var(--text-dim);font-size:0.82rem;margin:0 0 10px;">Two tiers on purpose: classification (flat UI vs. physical object) is a quick judgment — a fast, non-thinking model is fine. Building the actual replica benefits from a stronger, more careful model for the spatial/geometric reasoning involved, though neither model here is gated by an explicit thinking-mode toggle the way the text tiers above are — vision capability matters more than the reasoning slider in this section. Narrower provider choice than every setting above — vision-input support here is only verified for OpenAI and Gemini.</p>
      <div class="field-row">
        ${selectField('Provider', 'imageRecognitionProvider', IMAGE_RECOGNITION_PROVIDERS, s.imageRecognitionProvider || 'openai')}
      </div>
      <div class="field-row" style="margin-top:12px;">
        ${selectField('Classification model', 'imageRecognitionClassifyModel', modelOptionsFor(catalog, s.imageRecognitionProvider || 'openai'), s.imageRecognitionClassifyModel)}
        ${selectField('Replica-build model', 'imageRecognitionReplicaModel', modelOptionsFor(catalog, s.imageRecognitionProvider || 'openai'), s.imageRecognitionReplicaModel)}
      </div>

      <div class="admin-actions" style="margin-top:18px;">
        <button class="btn" id="test-generation-settings">Confirm model check</button>
        <button class="btn btn-primary" id="save-generation-settings" disabled>Save settings</button>
        <span id="admin-save-status" class="admin-save-status"></span>
      </div>
      <div id="model-check-results" class="model-check-results"></div>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Activity Snapshot</h3>
      <div class="admin-stat-grid">
        ${statCard('Users', stats.userCount || 0)}
        ${statCard('Courses', stats.courseCount || 0)}
        ${statCard('Topics', stats.topicCount || 0)}
        ${statCard('Error logs', stats.errorCount || 0)}
        ${statCard('Open complaints', stats.openComplaintCount || 0)}
      </div>
      <h3 style="margin-top:20px;">Recent Metered Usage</h3>
      <div class="admin-stat-grid">
        ${statCard('Requests', stats.usage?.requestCount || 0)}
        ${statCard('Input tokens', stats.usage?.inputTokens || 0)}
        ${statCard('Output tokens', stats.usage?.outputTokens || 0)}
      </div>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Library Complaints</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Buyer reports pause seller payout. Clear a report to release the held seller payout, or uphold it to refund the purchaser. You can also remove the listing and block the creator.</p>
      <div id="complaints-admin-wrap">
        ${complaintsTable(data.marketplaceComplaints || [])}
      </div>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Seller Bank Recoveries</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Sellers can request bank recovery only from released sale earnings. Wallet recharges are never reimbursable. Complete a request after you send the net amount to the seller's bank, or reject it to return the amount to their recoverable sale balance.</p>
      <div id="recoveries-admin-wrap">
        ${sellerRecoveriesTable(data.sellerRecoveries || [])}
      </div>
    </section>

    <section class="settings-panel">
      <h3>Pilot Access Settings</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Temporary switches for the school pilot. Both off restores the ordinary product (any signed-in user can generate; nothing auto-publishes).</p>
      <label style="display:flex;gap:8px;align-items:flex-start;margin-top:14px;">
        <input type="checkbox" id="access-generation-restricted" ${a.generationRestrictedToStaff ? 'checked' : ''} style="margin-top:3px;">
        <span>Only site admins, school owners, and teachers can generate content. Everyone else can still sign in, browse, and view already-owned content — this only blocks new generation.</span>
      </label>
      <label style="display:flex;gap:8px;align-items:flex-start;margin-top:10px;">
        <input type="checkbox" id="access-auto-share" ${a.autoShareStaffContent ? 'checked' : ''} style="margin-top:3px;">
        <span>Auto-publish everything a school owner or teacher generates to the marketplace, free (₹0), for 1 year, approved immediately — no manual "list" or "approve" step. Admin-generated content is never auto-published.</span>
      </label>
      <label style="display:flex;gap:8px;align-items:flex-start;margin-top:10px;">
        <input type="checkbox" id="access-time-windows-enabled" ${a.generationTimeWindowsEnabled ? 'checked' : ''} style="margin-top:3px;">
        <span>Only allow generation during specific hours on working days (Mon–Fri), IST — <strong>one schedule, applied to every school</strong>, not per-school. Site admins are always exempt.</span>
      </label>
      <div class="field-row" style="margin-top:8px;">
        <div class="field"><label>Window 1 start</label><input type="time" id="access-window1-start" value="${(a.generationTimeWindows?.[0]?.start) || '09:30'}" /></div>
        <div class="field"><label>Window 1 end</label><input type="time" id="access-window1-end" value="${(a.generationTimeWindows?.[0]?.end) || '11:30'}" /></div>
      </div>
      <div class="field-row" style="margin-top:8px;">
        <div class="field"><label>Window 2 start</label><input type="time" id="access-window2-start" value="${(a.generationTimeWindows?.[1]?.start) || '15:30'}" /></div>
        <div class="field"><label>Window 2 end</label><input type="time" id="access-window2-end" value="${(a.generationTimeWindows?.[1]?.end) || '23:59'}" /></div>
      </div>
      <div class="admin-actions">
        <button class="btn btn-primary" id="save-access-settings">Save access settings</button>
        <span id="access-save-status" class="admin-save-status"></span>
      </div>
    </section>

    <section class="settings-panel">
      <h3>Commerce Settings</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Every number that moves money is set here — nothing is hardcoded. Sellers see the marketplace commission only as "Processing fee". Schools are billed a flat wholesale price per student seat, per term (your only school revenue) — what a school then charges its own students is off-platform and never seen here.</p>
      <div class="field-row" style="margin-top:14px;">
        ${numField('Platform commission %', 'platformCommissionPct', c.platformCommissionPct)}
      </div>
      <p style="font-weight:700;margin:16px 0 4px;">School reseller pricing — your per-deal revenue</p>
      <div class="field-row">
        ${numField('Seat wholesale (₹ per seat / term)', 'seatWholesaleRupees', c.seatWholesaleRupees)}
        ${numField('Minimum charge (₹ per purchase)', 'minSchoolChargeRupees', c.minSchoolChargeRupees)}
      </div>
      <div class="field-row" style="margin-top:12px;">
        ${numField('Seat term (days)', 'seatTermDays', c.seatTermDays)}
        ${numField('School free generations / month (shared pool)', 'schoolFreeGenerationsPerMonth', c.schoolFreeGenerationsPerMonth)}
      </div>
      <p style="color:var(--text-dim);font-size:0.82rem;margin-top:4px;">Every school gets this many free generations per month, <strong>shared equally by its teachers</strong> — 2 teachers get half each, 4 get a quarter each. Beyond their share, generations bill the school wallet (the school must recharge to continue).</p>
      <p style="color:var(--text-dim);font-size:0.82rem;margin-top:4px;">A school pays <strong>max(seats × wholesale, minimum)</strong> each term. Set the minimum to 0 for pure per-seat pricing. What a school charges its own students is off-platform and never seen here.</p>
      <div class="field-row" style="margin-top:12px;">
        ${numField('Min recharge (₹)', 'minRechargeRupees', c.minRechargeRupees)}
        ${numField('Max recharge (₹)', 'maxRechargeRupees', c.maxRechargeRupees)}
      </div>
      <hr style="border:none;border-top:1px solid var(--border);margin:18px 0;">
      <p style="color:var(--text-dim);line-height:1.5;">Content creation is wallet-metered <strong>per AI provider API call</strong>, not per request — a generation that needs several retries/continuations costs proportionally more, automatically (see the "Average API calls per successful content generation" stat above for what that typically runs, to price this sensibly). A learner is blocked up front only if their balance can't cover a single call (0 = free); once a generation starts, its full actual cost is charged when it finishes, which can push the wallet negative for that one request if it needed several calls — the <strong>next</strong> generation is then blocked until they top up. Charges are <strong>never refunded</strong>, including for a failed or blocked generation — every attempt spends real provider budget regardless of outcome. This never affects access to content already purchased — that stays available for its full purchased duration regardless of wallet balance.</p>
      <div class="field-row" style="margin-top:12px;">
        ${numField('Content generation cost (₹ per API call)', 'generationCostRupees', c.generationCostRupees, '0.1')}
      </div>
      <p style="color:var(--text-dim);line-height:1.5;margin-top:14px;">Every brand-new individual account gets this many full generation <strong>requests</strong> free at signup — flat, one credit per request no matter how many API calls it takes internally (unlike the per-call wallet pricing above), enough to generate at least one simulation before ever funding a wallet. One-time only, never replenished; 0 disables it.</p>
      <div class="field-row" style="margin-top:12px;">
        ${numField('Free generations at signup', 'signupFreeGenerations', c.signupFreeGenerations, '1')}
      </div>
      <p style="color:var(--text-dim);line-height:1.5;margin-top:14px;">The in-page "Ask AI" bridge — dynamic content (a fresh practice problem, generated passage, etc.) or a quick Q&A inside an already-rendered page — is billed separately, usually lower than a full generation. This applies whether the page is a creator's own topic OR content a buyer purchased/filed: whoever is actually using the page pays, never the original creator.</p>
      <div class="field-row" style="margin-top:12px;">
        ${numField('Dynamic content / Ask AI cost (₹ per call)', 'askAiCostRupees', c.askAiCostRupees, '0.1')}
      </div>
      <p style="color:var(--text-dim);line-height:1.5;margin-top:14px;">The coding agent's chat-first turn — answering a question, making a small edit, or classifying a message as a real build request — runs BEFORE the full-generation billing below, on every single message. Billed separately (usually the lowest of the three) so this call is never free/unlimited.</p>
      <div class="field-row" style="margin-top:12px;">
        ${numField('Coding agent chat turn cost (₹ per call)', 'chatTurnCostRupees', c.chatTurnCostRupees, '0.1')}
      </div>
      <p style="color:var(--text-dim);line-height:1.5;margin-top:14px;">Seller bank recovery deducts the Razorpay/gateway fee from the seller's requested sale-earning amount. The remaining net amount is what admin should transfer to the seller's bank account.</p>
      <div class="field-row" style="margin-top:12px;">
        ${numField('Seller recovery Razorpay fee %', 'sellerRecoveryFeePct', c.sellerRecoveryFeePct, '0.01')}
        ${numField('Seller recovery fixed fee (Rs)', 'sellerRecoveryFixedFeeRupees', c.sellerRecoveryFixedFeeRupees, '0.1')}
      </div>
      <p style="color:var(--text-dim);line-height:1.5;margin-top:14px;">Viewing access (a learner's own generated content, purchases, and free/₹0 listings) is billed separately from generation and Ask AI, once every 30 days, auto-debited from the wallet (0 = free). Every new account gets one free 30-day period from signup before this first applies.</p>
      <div class="field-row" style="margin-top:12px;">
        ${numField('Monthly viewing access (₹, inclusive of taxes)', 'viewingSubscriptionRupees', c.viewingSubscriptionRupees, '1')}
      </div>
      <div class="admin-actions">
        <button class="btn btn-primary" id="save-commerce-settings">Save commerce settings</button>
        <button class="btn" id="sample-invoice-btn">🧾 Sample invoice</button>
        <span id="commerce-save-status" class="admin-save-status"></span>
      </div>
    </section>

    <section class="settings-panel">
      <h3>Wallet Credit (testing)</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Manually credit/debit a user's wallet — for testing purchases until the Razorpay gateway is connected, and for support corrections after.</p>
      <div class="field"><label>User email</label><input type="email" name="wcEmail" placeholder="user@example.com" /></div>
      <div class="field-row">
        <div class="field"><label>Amount (₹)</label><input type="number" name="wcAmount" min="1" placeholder="500" /></div>
        <label class="field"><span>Direction</span>
          <select name="wcDirection"><option value="credit">Credit</option><option value="debit">Debit</option></select>
        </label>
      </div>
      <div class="field"><label>Note (optional)</label><input type="text" name="wcNote" placeholder="Why" /></div>
      <div class="admin-actions">
        <button class="btn btn-primary" id="wallet-credit-btn">Apply</button>
        <span id="wallet-credit-status" class="admin-save-status"></span>
      </div>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Recommenders</h3>
      <p style="color:var(--text-dim);line-height:1.5;">A Recommender is a person who gets a school onboarded. They earn a commission — calculated as a % of <em>that school's own share</em> of each sale — for a fixed number of years, paid out of the platform's commission. The school's own earnings are never reduced by this. Each Recommender gets a unique code; that code is only ever entered on the school form below (School Partners), never given to students or used at checkout.</p>
      <div style="overflow:auto;">
        <table class="admin-table">
          <thead>
            <tr><th>Code</th><th>Name</th><th>Contact</th><th>Status</th><th>Commission</th><th>Duration</th><th>Schools</th><th>Sales</th><th>Wallet</th><th>Lifetime earned</th><th></th></tr>
          </thead>
          <tbody id="recommenders-tbody">
            ${(c.recommenders || []).map((r) => recommenderRow(r)).join('') || '<tr><td colspan="11">No recommenders yet — add the first one below.</td></tr>'}
          </tbody>
        </table>
      </div>
      <h3 style="margin-top:20px;" id="recommender-form-title">Add a recommender</h3>
      <div class="field-row">
        <div class="field"><label>Code (blank = auto-generate)</label><input type="text" name="recCode" placeholder="e.g. RAJESH2026" /></div>
        <div class="field"><label>Name</label><input type="text" name="recName" placeholder="Rajesh Kumar" /></div>
      </div>
      <div class="field-row" style="margin-top:12px;">
        <div class="field"><label>Email (optional)</label><input type="email" name="recEmail" placeholder="rajesh@example.com" /></div>
        <div class="field"><label>Phone (optional)</label><input type="text" name="recPhone" placeholder="+91 98765 43210" /></div>
      </div>
      <div class="field-row" style="margin-top:12px;">
        <div class="field"><label>Commission % (of the platform's seat revenue)</label><input type="number" name="recCommission" min="1" max="100" placeholder="20" /></div>
        <div class="field"><label>Duration (years, from onboarding)</label><input type="number" name="recDuration" min="1" max="50" placeholder="3" /></div>
        <label class="field"><span>Status</span>
          <select name="recEnabled"><option value="true">Enabled</option><option value="false">Disabled</option></select>
        </label>
      </div>
      <div class="admin-actions">
        <button class="btn btn-primary" id="save-recommender-btn">Save recommender</button>
        <span id="recommender-save-status" class="admin-save-status"></span>
      </div>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>School Partners</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Schools/colleges get a code (e.g. <code>RishikulGohana</code>). They buy student seats (billed to their prepaid wallet at the seat wholesale price above) and resell access to their students at whatever price they choose — off-platform. Fund a school's wallet below; the school owner then buys seats and toggles student content-creation from their own dashboard. If the school came directly, leave introduced-by blank.</p>
      <div style="overflow:auto;">
        <table class="admin-table">
          <thead>
            <tr><th>Code</th><th>Name</th><th>Owner</th><th>Status</th><th>Seats</th><th>Term ends</th><th>Gen</th><th title="Whether this school's students may sell their creations in the Library">Library</th><th title="Curated labels seeded as pinned, free access for this school's students — see Curated Labels above">Labels</th><th>Introduced by</th><th>Wallet</th><th></th></tr>
          </thead>
          <tbody id="schools-tbody">
            ${(c.schools || []).map((s) => schoolRow(s, data.marketplaceLabels || [])).join('') || '<tr><td colspan="12">No schools yet — add the first one below.</td></tr>'}
          </tbody>
        </table>
      </div>
      <span class="admin-save-status" id="school-marketplace-status"></span>
      <h3 style="margin-top:20px;" id="school-form-title">Add a school</h3>
      <div class="field-row">
        <div class="field"><label>Code</label><input type="text" name="schCode" placeholder="RishikulGohana" /></div>
        <div class="field"><label>Name</label><input type="text" name="schName" placeholder="Rishikul Vidyapeeth, Gohana" /></div>
        <div class="field"><label>Owner account email (for their dashboard)</label><input type="email" name="schOwner" placeholder="principal@school.edu" /></div>
      </div>
      <div class="field-row" style="margin-top:12px;">
        <label class="field"><span>Status</span>
          <select name="schEnabled"><option value="true">Enabled</option><option value="false">Disabled</option></select>
        </label>
      </div>
      <div class="field-row" style="margin-top:12px;">
        <label class="field">
          <span>Introduced by (blank = direct school onboarding)</span>
          <select name="schRecommender">
            <option value="">Direct onboarding</option>
            ${(c.recommenders || []).map((r) => `<option value="${escapeHtml(r.code)}">${escapeHtml(r.name)} (${escapeHtml(r.code)}) — ${r.commissionPct}% for ${r.durationYears}y</option>`).join('')}
          </select>
        </label>
      </div>
      <div class="admin-actions">
        <button class="btn btn-primary" id="save-school-btn">Save school</button>
        <span id="school-save-status" class="admin-save-status"></span>
      </div>

      <hr style="border:none;border-top:1px solid var(--border);margin:18px 0;">
      <h3 style="margin:0 0 4px;">Fund a school wallet</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Credit the school's prepaid pool per your deal with them. They spend it on seats and (if enabled) student generation. Use "debit" to correct an over-credit.</p>
      <div class="field-row">
        <div class="field"><label>School code</label>
          <input type="text" name="swCode" list="sw-school-list" placeholder="RishikulGohana" />
          <datalist id="sw-school-list">${(c.schools || []).map((s) => `<option value="${escapeHtml(s.code)}">${escapeHtml(s.name)}</option>`).join('')}</datalist>
        </div>
        <div class="field"><label>Amount (₹)</label><input type="number" name="swAmount" min="1" /></div>
        <label class="field"><span>Direction</span>
          <select name="swDirection"><option value="credit">Credit</option><option value="debit">Debit</option></select>
        </label>
      </div>
      <div class="field"><label>Note (optional)</label><input type="text" name="swNote" placeholder="e.g. Term 1 2026 plan" /></div>
      <div class="admin-actions">
        <button class="btn btn-primary" id="credit-school-btn">Apply to school wallet</button>
        <span id="school-credit-status" class="admin-save-status"></span>
      </div>
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Landing Page Showcase</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Feature real created content in the landing page's sample gallery, alongside the built-in animations. Enter active marketplace listing IDs (one per line, in display order, up to 24). Each contributes one approved topic, rendered live in a sandboxed frame. Delisted entries are skipped automatically.</p>
      <div class="field">
        <label>Featured listing IDs (one per line)</label>
        <textarea name="showcaseIds" rows="5" style="width:100%;font-family:monospace;">${escapeHtml((data.landingShowcase?.listingIds || []).join('\n'))}</textarea>
      </div>
      <div class="admin-actions">
        <button class="btn btn-primary" id="save-showcase-btn">Save showcase</button>
        <span id="showcase-save-status" class="admin-save-status"></span>
      </div>
      ${(data.landingShowcase?.activeListings || []).length ? `
      <h3 style="margin-top:20px;">Active listings (copy IDs from here)</h3>
      <div style="overflow:auto;max-height:260px;">
        <table class="admin-table">
          <thead><tr><th>Listing ID</th><th>Title</th><th>Type</th></tr></thead>
          <tbody>
            ${(data.landingShowcase.activeListings).map((l) => `
              <tr><td style="font-family:monospace;">${escapeHtml(l.id)}</td><td>${escapeHtml(l.title)}</td><td>${escapeHtml(l.type)}</td></tr>`).join('')}
          </tbody>
        </table>
      </div>` : '<p style="color:var(--text-dim);font-size:0.86rem;">No active marketplace listings yet — content published for sale (including free listings) will appear here.</p>'}
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>Curated Labels</h3>
      <p style="color:var(--text-dim);line-height:1.5;">Tag Library listings as recommended — each label shows up as its own featured section at the top of Library Browse, but only for accounts linked to a school (owner, teacher, or student), never for an unaffiliated individual account. Name the first one "Preferred list" (or anything that reads better); create more later if useful.</p>
      <div id="mp-labels-list"></div>
      <div class="admin-actions">
        <button class="btn" id="add-label-btn">+ Add label</button>
      </div>
      ${(data.landingShowcase?.activeListings || []).length ? `
      <h3 style="margin-top:20px;">Active listings (copy IDs from here)</h3>
      <div style="overflow:auto;max-height:260px;">
        <table class="admin-table">
          <thead><tr><th>Listing ID</th><th>Title</th><th>Type</th></tr></thead>
          <tbody>
            ${(data.landingShowcase.activeListings).map((l) => `
              <tr><td style="font-family:monospace;">${escapeHtml(l.id)}</td><td>${escapeHtml(l.title)}</td><td>${escapeHtml(l.type)}</td></tr>`).join('')}
          </tbody>
        </table>
      </div>` : ''}
    </section>

    <section class="settings-panel" style="grid-column:1/-1;">
      <h3>All Registered Users <span style="color:var(--text-dim);font-weight:400;font-size:0.85rem;">(${formatNumber((data.recentUsers || []).length)})</span></h3>
      <p style="color:var(--text-dim);font-size:0.82rem;margin-top:-4px;">Every account registered in Firebase Auth, sorted by most recently active (then most recently signed up). Tick <strong>Unlimited</strong> to exempt a user from all wallet metering — generation, Ask AI, and monthly viewing all become free for them, with no balance ever touched. Ticking it on a <span class="pill">🏫 School</span> row automatically covers every teacher that school lists too — nothing to set per teacher. Click any row to see everything that user has created (every animation/simulation) and how many API requests each one has used.</p>
      <p style="color:var(--text-dim);font-size:0.82rem;">Platform total: <strong>${formatNumber(stats.courseCount)}</strong> courses, <strong>${formatNumber(stats.topicCount)}</strong> topics (animations/simulations) · <strong>${formatNumber(stats.usage?.requestCount)}</strong> total API requests · <strong>${(stats.avgRequestsPerContent || 0).toFixed(2)}</strong> average requests per content item.</p>
      <div style="overflow:auto;max-height:520px;">
        <table class="admin-table">
          <thead>
            <tr><th title="Unlimited access">✓</th><th>User</th><th>Email</th><th>Content</th><th>Requests</th><th>Input tokens</th><th>Output tokens</th><th>Signed up</th><th>Last request</th></tr>
          </thead>
          <tbody>
            ${(data.recentUsers || []).map((u) => `
              <tr data-uid="${escapeHtml(u.uid)}" data-name="${escapeHtml(u.displayName || u.uid)}" data-email="${escapeHtml(u.email || '')}" class="admin-user-row" style="cursor:pointer;" title="Click to see this user's content">
                <td style="text-align:center;">
                  <input type="checkbox" class="unlimited-access-toggle" ${u.unlimitedAccess ? 'checked' : ''} title="Unlimited access — bypasses all wallet metering">
                </td>
                <td>${escapeHtml(u.displayName || u.uid)}${u.isSchoolOwner
                  ? ` <span class="pill" title="${escapeHtml(u.schoolName || u.schoolCode || '')}">🏫 School</span>`
                  : u.isTeacher
                    ? ` <span class="pill" title="Teacher at ${escapeHtml(u.schoolName || u.schoolCode || '')}">👩‍🏫 Teacher</span>`
                    : ''}</td>
                <td>${escapeHtml(u.email || '')}</td>
                <td>${formatNumber(u.contentCount)} <span style="color:var(--text-dim);font-size:0.78rem;">(${formatNumber(u.courseCount)}c / ${formatNumber(u.topicCount)}t)</span></td>
                <td>${formatNumber(u.requestCount)}</td>
                <td>${formatNumber(u.inputTokens)}</td>
                <td>${formatNumber(u.outputTokens)}</td>
                <td>${u.createdAt ? new Date(u.createdAt).toLocaleString() : ''}</td>
                <td>${u.lastRequestAt ? new Date(u.lastRequestAt).toLocaleString() : ''}</td>
              </tr>
            `).join('') || '<tr><td colspan="9">No users yet.</td></tr>'}
          </tbody>
        </table>
      </div>
      <span class="admin-save-status" id="unlimited-access-status"></span>
    </section>
  `;
  body.querySelector('#open-admin-test-area').addEventListener('click', () => navigate('/dwar/test-area'));
  wireEmbeddingBackfillControls(body);
  wireTopicOwnerBackfillControls(body);

  wireProviderModelControls(body, catalog, 'default');
  wireProviderModelControls(body, catalog, 'agentModel', 'agentModel');
  wireProviderModelControls(body, catalog, 'verification');
  wireProviderModelControls(body, catalog, 'askAi');
  wireImageProviderControl(body, imageCatalog);
  wireImageRecognitionProviderControl(body, catalog);
  wireCommerceControls(body, c);
  wireShowcaseControls(body);
  wireLabelsControls(body, data.marketplaceLabels);
  wireComplaintControls(body);
  wireSellerRecoveryControls(body);
  wireRecentUsersControls(body);

  let confirmedSignature = '';
  const status = body.querySelector('#admin-save-status');
  const saveBtn = body.querySelector('#save-generation-settings');
  const testBtn = body.querySelector('#test-generation-settings');
  const results = body.querySelector('#model-check-results');

  const resetCheckButton = () => {
    testBtn.classList.remove('btn-success', 'btn-danger');
    testBtn.textContent = 'Confirm model check';
  };
  const markDirty = () => {
    confirmedSignature = '';
    saveBtn.disabled = true;
    resetCheckButton();
    results.innerHTML = '';
    status.className = 'admin-save-status';
    status.textContent = 'Run the model check before saving.';
  };
  body.querySelectorAll('select[name$="Provider"], select[name$="Model"], select[name$="ReasoningEffort"], select[name="imageQuality"], select[name="imageSize"]').forEach((select) => {
    select.addEventListener('change', markDirty);
  });

  testBtn.addEventListener('click', async () => {
    const settings = readSettings(body);
    const signature = settingsSignature(settings);
    testBtn.disabled = true;
    saveBtn.disabled = true;
    resetCheckButton();
    testBtn.textContent = 'Checking…';
    results.innerHTML = '';
    status.className = 'admin-save-status';
    status.textContent = 'Checking every configured model…';
    try {
      const result = await testGenerationSettings(settings);
      const checks = result.checks || [];
      renderCheckResults(results, checks);
      const failed = checks.filter((c) => c.ok === false);
      const allOk = result.ok !== false && failed.length === 0;
      if (allOk) {
        confirmedSignature = signature;
        testBtn.classList.add('btn-success');
        testBtn.textContent = 'Models working ✓';
        status.className = 'admin-save-status is-ok';
        status.textContent = 'All configured models are working — you can save now.';
        saveBtn.disabled = false;
      } else {
        confirmedSignature = '';
        testBtn.classList.add('btn-danger');
        testBtn.textContent = 'Confirm model check';
        const names = failed.map((c) => slotLabel(c.slot)).join(' and ');
        status.className = 'admin-save-status is-fail';
        status.textContent = names
          ? `Not working: ${names}. See details below.`
          : 'Model check failed. See details below.';
      }
    } catch (err) {
      confirmedSignature = '';
      testBtn.classList.add('btn-danger');
      testBtn.textContent = 'Confirm model check';
      results.innerHTML = '';
      status.className = 'admin-save-status is-fail';
      status.textContent = err.message || 'Model check failed.';
    } finally {
      testBtn.disabled = false;
    }
  });

  saveBtn.addEventListener('click', async () => {
    const settings = readSettings(body);
    if (settingsSignature(settings) !== confirmedSignature) {
      status.className = 'admin-save-status is-fail';
      status.textContent = 'Run the model check before saving these settings.';
      saveBtn.disabled = true;
      return;
    }
    status.className = 'admin-save-status';
    status.textContent = 'Saving…';
    try {
      const saved = await updateGenerationSettings(settings);
      status.className = 'admin-save-status is-ok';
      status.textContent = 'Saved.';
      confirmedSignature = settingsSignature(saved);
      setProviderGroupValues(body, catalog, 'default', saved.defaultProvider, saved.defaultModel, saved.defaultReasoningEffort || '');
      setProviderGroupValues(body, catalog, 'agentModel', saved.agentModelProvider, saved.agentModel, saved.agentModelReasoningEffort || '', 'agentModel');
      setProviderGroupValues(body, catalog, 'verification', saved.verificationProvider, saved.verificationModel, saved.verificationReasoningEffort || '');
      setProviderGroupValues(body, catalog, 'askAi', saved.askAiProvider, saved.askAiModel, saved.askAiReasoningEffort || '');
      body.querySelector('[name="imageProvider"]').value = saved.imageProvider;
      fillSelect(body.querySelector('[name="imageModel"]'), modelOptionsFor(imageCatalog, saved.imageProvider || 'openai'), saved.imageModel);
      body.querySelector('[name="imageQuality"]').value = saved.imageQuality;
      body.querySelector('[name="imageSize"]').value = saved.imageSize;
      body.querySelector('[name="imageRecognitionProvider"]').value = saved.imageRecognitionProvider;
      fillSelect(body.querySelector('[name="imageRecognitionClassifyModel"]'), modelOptionsFor(catalog, saved.imageRecognitionProvider), saved.imageRecognitionClassifyModel);
      fillSelect(body.querySelector('[name="imageRecognitionReplicaModel"]'), modelOptionsFor(catalog, saved.imageRecognitionProvider), saved.imageRecognitionReplicaModel);
    } catch (err) {
      status.className = 'admin-save-status is-fail';
      status.textContent = err.message || 'Could not save.';
    }
  });
}

// ── Commerce wiring ───────────────────────────────────────────────────────

function numField(label, name, value, step) {
  return `
    <label class="field">
      <span>${escapeHtml(label)}</span>
      <input type="number" name="${name}" value="${escapeHtml(String(value ?? ''))}" min="0" ${step ? `step="${escapeHtml(String(step))}"` : ''} />
    </label>
  `;
}

function schoolRow(s, labels) {
  const pinned = s.pinnedLabelIds || [];
  return `<tr class="school-row" data-code="${escapeHtml(s.code)}" style="cursor:pointer;" title="Click to edit">
    <td><code>${escapeHtml(s.code)}</code></td>
    <td>${escapeHtml(s.name)}</td>
    <td>${escapeHtml(s.ownerEmail || '—')}</td>
    <td>${s.enabled ? 'Enabled' : '⛔ Disabled'}${s.seatActive ? '' : ' <span style="color:var(--danger);font-size:0.75rem;">no seats</span>'}</td>
    <td>${formatNumber(s.studentCount || 0)}/${formatNumber(s.seatsLicensed || 0)}</td>
    <td>${s.seatTermEndsAt ? new Date(s.seatTermEndsAt).toLocaleDateString() : '—'}</td>
    <td>${s.studentsCanGenerate ? 'On' : 'Off'}</td>
    <td style="text-align:center;">
      <input type="checkbox" class="marketplace-selling-toggle" ${s.marketplaceSellingEnabled ? 'checked' : ''} title="Allow this school's students to sell their creations in the marketplace">
    </td>
    <td>
      ${labels.length === 0 ? '<span style="color:var(--text-dim);font-size:0.78rem;">none created yet</span>' : `
      <select multiple class="school-labels-select" data-code="${escapeHtml(s.code)}" size="${Math.min(3, labels.length)}" style="min-width:130px;font-size:0.82rem;">
        ${labels.map((l) => `<option value="${escapeHtml(l.id)}" ${pinned.includes(l.id) ? 'selected' : ''}>${escapeHtml(l.name)}</option>`).join('')}
      </select>
      <button class="btn btn-sm school-labels-save-btn" data-code="${escapeHtml(s.code)}" title="Save — provisions free access and pins for this school's current students">💾</button>`}
    </td>
    <td>${s.recommenderCode ? `<code>${escapeHtml(s.recommenderCode)}</code>${s.recommenderLinkedAt ? `<br><span style="color:var(--text-dim);font-size:0.75rem;">since ${new Date(s.recommenderLinkedAt).toLocaleDateString()}</span>` : ""}` : "Direct"}</td>
    <td>${formatPaise(s.balancePaise)}</td>
    <td>✏️</td>
  </tr>`;
}

function recommenderRow(r) {
  return `<tr class="recommender-row" data-code="${escapeHtml(r.code)}" style="cursor:pointer;" title="Click to edit">
    <td><code>${escapeHtml(r.code)}</code></td>
    <td>${escapeHtml(r.name)}</td>
    <td>${escapeHtml(r.email || r.phone || '—')}</td>
    <td>${r.enabled ? 'Enabled' : '⛔ Disabled'}</td>
    <td>${r.commissionPct}% of seat revenue</td>
    <td>${r.durationYears} year${r.durationYears === 1 ? '' : 's'}</td>
    <td>${formatNumber(r.schoolCount)}</td>
    <td>${formatNumber(r.salesCount)}</td>
    <td>${formatPaise(r.balancePaise)}</td>
    <td>${formatPaise(r.totalEarnedPaise)}</td>
    <td>✏️</td>
  </tr>`;
}

function complaintsTable(complaints) {
  if (!complaints.length) {
    return '<div class="empty-state"><p>No open marketplace complaints.</p></div>';
  }
  return `
    <div style="overflow:auto;">
      <table class="admin-table">
        <thead><tr><th>When</th><th>Item</th><th>Complaint</th><th>Paid</th><th>Controls</th></tr></thead>
        <tbody>
          ${complaints.map((c) => `
            <tr data-complaint-id="${escapeHtml(c.id)}">
              <td>${c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}</td>
              <td>${escapeHtml(c.listingEmoji || '')} ${escapeHtml(c.listingTitle || 'Untitled')}<br><span style="color:var(--text-dim);font-size:0.78rem;">Seller: ${escapeHtml(c.sellerUid || '')}</span></td>
              <td style="white-space:normal;min-width:260px;">${escapeHtml(c.complaint || '')}</td>
              <td>${formatPaise(c.pricePaidPaise || 0)}</td>
              <td>
                <div style="display:flex;flex-direction:column;gap:6px;min-width:220px;">
                  <textarea rows="2" class="complaint-note" placeholder="Admin note"></textarea>
                  <label style="display:flex;gap:6px;align-items:center;"><input type="checkbox" class="complaint-remove" /> Remove listing</label>
                  <label style="display:flex;gap:6px;align-items:center;"><input type="checkbox" class="complaint-block" /> Block creator</label>
                  <div style="display:flex;gap:6px;flex-wrap:wrap;">
                    <button class="btn btn-sm complaint-clear">Clear and release</button>
                    <button class="btn btn-sm btn-danger complaint-refund">Refund purchaser</button>
                  </div>
                  <span class="admin-save-status complaint-status"></span>
                </div>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function wireComplaintControls(body) {
  body.querySelectorAll('[data-complaint-id]').forEach((row) => {
    const run = async (outcome) => {
      const status = row.querySelector('.complaint-status');
      const buttons = row.querySelectorAll('button');
      buttons.forEach((b) => { b.disabled = true; });
      status.className = 'admin-save-status';
      status.textContent = 'Applying...';
      try {
        await resolveMarketplaceComplaint({
          complaintId: row.dataset.complaintId,
          outcome,
          note: row.querySelector('.complaint-note').value.trim(),
          removeListing: row.querySelector('.complaint-remove').checked,
          blockCreator: row.querySelector('.complaint-block').checked,
        });
        status.className = 'admin-save-status is-ok';
        status.textContent = 'Resolved. Reload to refresh the queue.';
      } catch (err) {
        status.className = 'admin-save-status is-fail';
        status.textContent = err.message || 'Could not resolve.';
        buttons.forEach((b) => { b.disabled = false; });
      }
    };
    row.querySelector('.complaint-clear').addEventListener('click', () => run('clear'));
    row.querySelector('.complaint-refund').addEventListener('click', () => run('refund'));
  });
}

function sellerRecoveriesTable(recoveries) {
  if (!recoveries.length) {
    return '<div class="empty-state"><h3>No pending recoveries</h3><p>Seller bank recovery requests will appear here.</p></div>';
  }
  return `
    <div style="overflow:auto;">
      <table class="admin-table">
        <thead><tr><th>When</th><th>Seller</th><th>Bank</th><th style="text-align:right;">Requested</th><th style="text-align:right;">Fee</th><th style="text-align:right;">Net transfer</th><th>Controls</th></tr></thead>
        <tbody>
          ${recoveries.map((r) => `
            <tr data-recovery-id="${escapeHtml(r.id)}">
              <td>${r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</td>
              <td>${escapeHtml(r.uid || '')}</td>
              <td style="white-space:normal;min-width:240px;">
                <strong>${escapeHtml(r.bank?.accountName || '')}</strong><br>
                ${escapeHtml(r.bank?.bankName || '')}<br>
                A/C ${escapeHtml(r.bank?.accountNumber || '')}<br>
                IFSC ${escapeHtml(r.bank?.ifsc || '')}
              </td>
              <td style="text-align:right;">${formatPaise(r.amountPaise || 0)}</td>
              <td style="text-align:right;">-${formatPaise(r.razorpayFeePaise || 0)}</td>
              <td style="text-align:right;font-weight:800;color:var(--success);">${formatPaise(r.netPaise || 0)}</td>
              <td>
                <div style="display:flex;flex-direction:column;gap:6px;min-width:220px;">
                  <textarea rows="2" class="recovery-note" placeholder="Admin note / transfer reference"></textarea>
                  <div style="display:flex;gap:6px;flex-wrap:wrap;">
                    <button class="btn btn-sm recovery-complete">Mark transferred</button>
                    <button class="btn btn-sm btn-danger recovery-reject">Reject and return</button>
                  </div>
                  <span class="admin-save-status recovery-status"></span>
                </div>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function wireSellerRecoveryControls(body) {
  body.querySelectorAll('[data-recovery-id]').forEach((row) => {
    const run = async (outcome) => {
      const status = row.querySelector('.recovery-status');
      const buttons = row.querySelectorAll('button');
      buttons.forEach((b) => { b.disabled = true; });
      status.className = 'admin-save-status';
      status.textContent = 'Applying...';
      try {
        await resolveSellerRecovery({
          recoveryRequestId: row.dataset.recoveryId,
          outcome,
          note: row.querySelector('.recovery-note').value.trim(),
        });
        status.className = 'admin-save-status is-ok';
        status.textContent = 'Resolved. Reload to refresh the queue.';
      } catch (err) {
        status.className = 'admin-save-status is-fail';
        status.textContent = err.message || 'Could not resolve.';
        buttons.forEach((b) => { b.disabled = false; });
      }
    };
    row.querySelector('.recovery-complete').addEventListener('click', () => run('complete'));
    row.querySelector('.recovery-reject').addEventListener('click', () => run('reject'));
  });
}

function wireRecentUsersControls(body) {
  const status = body.querySelector('#unlimited-access-status');

  // Click a user row (anywhere except the unlimited-access checkbox) to
  // drill into everything they've created.
  body.querySelectorAll('.admin-user-row').forEach((row) => {
    row.addEventListener('click', (e) => {
      if (e.target.closest('.unlimited-access-toggle')) return;
      const { uid, name, email } = row.dataset;
      openUserContentModal(uid, name, email);
    });
  });

  body.querySelectorAll('.unlimited-access-toggle').forEach((checkbox) => {
    checkbox.addEventListener('change', async () => {
      const row = checkbox.closest('[data-uid]');
      const uid = row?.dataset.uid;
      const unlimited = checkbox.checked;
      checkbox.disabled = true;
      if (status) {
        status.className = 'admin-save-status';
        status.textContent = `${unlimited ? 'Granting' : 'Revoking'} unlimited access...`;
      }
      try {
        await setUnlimitedAccess({ uid, unlimited });
        if (status) {
          status.className = 'admin-save-status is-ok';
          status.textContent = `${unlimited ? 'Unlimited access granted.' : 'Unlimited access revoked.'}`;
        }
      } catch (err) {
        checkbox.checked = !unlimited; // revert on failure
        if (status) {
          status.className = 'admin-save-status is-fail';
          status.textContent = friendlyError(err, 'Could not update unlimited access.');
        }
      } finally {
        checkbox.disabled = false;
      }
    });
  });
}

function wireShowcaseControls(body) {
  const status = body.querySelector('#showcase-save-status');
  body.querySelector('#save-showcase-btn')?.addEventListener('click', async () => {
    status.className = 'admin-save-status';
    status.textContent = 'Saving…';
    try {
      const ids = body.querySelector('[name="showcaseIds"]').value
        .split('\n').map((s) => s.trim()).filter(Boolean);
      const res = await updateLandingShowcase(ids);
      status.className = 'admin-save-status is-ok';
      status.textContent = `Saved — ${res.listingIds.length} listing${res.listingIds.length === 1 ? '' : 's'} featured on the landing page.`;
    } catch (err) {
      status.className = 'admin-save-status is-fail';
      status.textContent = err.message || 'Could not save.';
    }
  });
}

// Curated Labels editor — see functions/marketplaceLabels.js. Each row is
// wired independently (own Save/Delete), so saving one label never touches
// the others. A brand-new row (added via "+ Add label") has no data-label-
// id yet; on its first successful save we stamp the id the server just
// assigned so subsequent saves on that same row update rather than
// duplicate.
function labelRowHtml(label) {
  return `
    <div class="field-row" data-label-row data-label-id="${escapeHtml(label.id || '')}" style="align-items:flex-start;margin-top:10px;padding-top:10px;border-top:1px solid var(--border);">
      <div class="field" style="flex:1;">
        <label>Label name</label>
        <input type="text" class="label-name" placeholder="e.g. Preferred list" value="${escapeHtml(label.name || '')}" />
      </div>
      <div class="field" style="flex:2;">
        <label>Listing IDs (one per line)</label>
        <textarea class="label-listing-ids" rows="3" style="width:100%;font-family:monospace;">${escapeHtml((label.listingIds || []).join('\n'))}</textarea>
      </div>
      <div style="display:flex;flex-direction:column;gap:6px;align-self:flex-end;">
        <button class="btn btn-sm btn-primary label-save-btn">Save</button>
        <button class="btn btn-sm btn-ghost label-delete-btn" ${label.id ? '' : 'disabled'}>Delete</button>
      </div>
      <div class="label-status" style="flex-basis:100%;font-size:0.82rem;min-height:1.2em;"></div>
    </div>
  `;
}

function wireLabelRow(row) {
  const status = row.querySelector('.label-status');
  row.querySelector('.label-save-btn').addEventListener('click', async () => {
    status.style.color = '';
    status.textContent = 'Saving…';
    try {
      const name = row.querySelector('.label-name').value.trim();
      const listingIds = row.querySelector('.label-listing-ids').value.split('\n').map((s) => s.trim()).filter(Boolean);
      const labelId = row.dataset.labelId || undefined;
      const res = await saveMarketplaceLabel({ labelId, name, listingIds });
      row.dataset.labelId = res.id;
      row.querySelector('.label-delete-btn').disabled = false;
      status.style.color = 'var(--success)';
      status.textContent = `Saved — ${res.listingIds.length} listing${res.listingIds.length === 1 ? '' : 's'}.`;
    } catch (err) {
      status.style.color = 'var(--danger)';
      status.textContent = friendlyError(err, 'Could not save.');
    }
  });
  row.querySelector('.label-delete-btn').addEventListener('click', async () => {
    const labelId = row.dataset.labelId;
    if (!labelId) return;
    const ok = await confirmDialog(`Delete the "${row.querySelector('.label-name').value.trim() || 'this'}" label? It stops showing to schools immediately.`, { title: 'Delete label' });
    if (!ok) return;
    try {
      await deleteMarketplaceLabel(labelId);
      row.remove();
    } catch (err) {
      status.style.color = 'var(--danger)';
      status.textContent = friendlyError(err, 'Could not delete.');
    }
  });
}

function wireLabelsControls(body, labels) {
  const list = body.querySelector('#mp-labels-list');
  (labels || []).forEach((label) => {
    list.insertAdjacentHTML('beforeend', labelRowHtml(label));
    wireLabelRow(list.lastElementChild);
  });
  body.querySelector('#add-label-btn').addEventListener('click', () => {
    list.insertAdjacentHTML('beforeend', labelRowHtml({}));
    wireLabelRow(list.lastElementChild);
    list.lastElementChild.querySelector('.label-name').focus();
  });
}

function wireCommerceControls(body, c) {
  // Save commerce settings.
  const accessStatus = body.querySelector('#access-save-status');
  body.querySelector('#save-access-settings').addEventListener('click', async () => {
    accessStatus.className = 'admin-save-status';
    accessStatus.textContent = 'Saving…';
    try {
      await updateAccessSettings({
        generationRestrictedToStaff: body.querySelector('#access-generation-restricted').checked,
        autoShareStaffContent: body.querySelector('#access-auto-share').checked,
        generationTimeWindowsEnabled: body.querySelector('#access-time-windows-enabled').checked,
        generationTimeWindows: [
          { start: body.querySelector('#access-window1-start').value, end: body.querySelector('#access-window1-end').value },
          { start: body.querySelector('#access-window2-start').value, end: body.querySelector('#access-window2-end').value },
        ],
      });
      accessStatus.className = 'admin-save-status is-ok';
      accessStatus.textContent = 'Saved — applies immediately, no deploy needed.';
    } catch (err) {
      accessStatus.className = 'admin-save-status is-fail';
      accessStatus.textContent = friendlyError(err, 'Could not save.');
    }
  });

  const commerceStatus = body.querySelector('#commerce-save-status');
  body.querySelector('#save-commerce-settings').addEventListener('click', async () => {
    commerceStatus.className = 'admin-save-status';
    commerceStatus.textContent = 'Saving…';
    try {
      await updateCommerceSettings({
        platformCommissionPct: Number(body.querySelector('[name="platformCommissionPct"]').value),
        seatWholesaleRupees: Number(body.querySelector('[name="seatWholesaleRupees"]').value),
        minSchoolChargeRupees: Number(body.querySelector('[name="minSchoolChargeRupees"]').value),
        seatTermDays: Number(body.querySelector('[name="seatTermDays"]').value),
        schoolFreeGenerationsPerMonth: Number(body.querySelector('[name="schoolFreeGenerationsPerMonth"]').value),
        minRechargeRupees: Number(body.querySelector('[name="minRechargeRupees"]').value),
        maxRechargeRupees: Number(body.querySelector('[name="maxRechargeRupees"]').value),
        generationCostRupees: Number(body.querySelector('[name="generationCostRupees"]').value),
        askAiCostRupees: Number(body.querySelector('[name="askAiCostRupees"]').value),
        chatTurnCostRupees: Number(body.querySelector('[name="chatTurnCostRupees"]').value),
        signupFreeGenerations: Number(body.querySelector('[name="signupFreeGenerations"]').value),
        sellerRecoveryFeePct: Number(body.querySelector('[name="sellerRecoveryFeePct"]').value),
        sellerRecoveryFixedFeeRupees: Number(body.querySelector('[name="sellerRecoveryFixedFeeRupees"]').value),
        viewingSubscriptionRupees: Number(body.querySelector('[name="viewingSubscriptionRupees"]').value),
      });
      commerceStatus.className = 'admin-save-status is-ok';
      commerceStatus.textContent = 'Saved — applies to every purchase from now on.';
    } catch (err) {
      commerceStatus.className = 'admin-save-status is-fail';
      commerceStatus.textContent = err.message || 'Could not save.';
    }
  });

  // Sample invoice — server-side previewInvoice runs the REAL split code.
  body.querySelector('#sample-invoice-btn').addEventListener('click', () => openSampleInvoiceModal(c));

  // Wallet credit.
  const wcStatus = body.querySelector('#wallet-credit-status');
  body.querySelector('#wallet-credit-btn').addEventListener('click', async () => {
    wcStatus.className = 'admin-save-status';
    wcStatus.textContent = 'Applying…';
    try {
      const res = await adminCreditWallet({
        email: body.querySelector('[name="wcEmail"]').value.trim(),
        amountRupees: Number(body.querySelector('[name="wcAmount"]').value),
        direction: body.querySelector('[name="wcDirection"]').value,
        note: body.querySelector('[name="wcNote"]').value.trim(),
      });
      wcStatus.className = 'admin-save-status is-ok';
      wcStatus.textContent = `Done — new balance ${formatPaise(res.balancePaise)}.`;
    } catch (err) {
      wcStatus.className = 'admin-save-status is-fail';
      wcStatus.textContent = err.message || 'Could not apply.';
    }
  });

  // School save + click-a-row-to-edit.
  const schoolStatus = body.querySelector('#school-save-status');
  body.querySelector('#save-school-btn').addEventListener('click', async () => {
    schoolStatus.className = 'admin-save-status';
    schoolStatus.textContent = 'Saving…';
    try {
      await upsertSchool({
        code: body.querySelector('[name="schCode"]').value.trim(),
        name: body.querySelector('[name="schName"]').value.trim(),
        ownerEmail: body.querySelector('[name="schOwner"]').value.trim() || null,
        recommenderCode: body.querySelector('[name="schRecommender"]').value.trim() || null,
        enabled: body.querySelector('[name="schEnabled"]').value === 'true',
      });
      schoolStatus.className = 'admin-save-status is-ok';
      schoolStatus.textContent = 'Saved. Reload the dashboard to see the updated table.';
    } catch (err) {
      schoolStatus.className = 'admin-save-status is-fail';
      schoolStatus.textContent = err.message || 'Could not save school.';
    }
  });

  // Fund/adjust a school's prepaid wallet.
  const schoolCreditStatus = body.querySelector('#school-credit-status');
  body.querySelector('#credit-school-btn').addEventListener('click', async () => {
    schoolCreditStatus.className = 'admin-save-status';
    schoolCreditStatus.textContent = 'Applying…';
    try {
      const res = await adminCreditSchoolWallet({
        code: body.querySelector('[name="swCode"]').value.trim(),
        amountRupees: Number(body.querySelector('[name="swAmount"]').value),
        direction: body.querySelector('[name="swDirection"]').value,
        note: body.querySelector('[name="swNote"]').value.trim(),
      });
      schoolCreditStatus.className = 'admin-save-status is-ok';
      schoolCreditStatus.textContent = `Done. School wallet balance: ${formatPaise(res.balancePaise)}. Reload to refresh the table.`;
    } catch (err) {
      schoolCreditStatus.className = 'admin-save-status is-fail';
      schoolCreditStatus.textContent = err.message || 'Could not update the school wallet.';
    }
  });

  body.querySelectorAll('.school-row').forEach((row) => {
    row.addEventListener('click', (e) => {
      if (e.target.closest('.marketplace-selling-toggle') || e.target.closest('.school-labels-select') || e.target.closest('.school-labels-save-btn')) return;
      const school = (c.schools || []).find((x) => x.code === row.dataset.code);
      if (!school) return;
      body.querySelector('#school-form-title').textContent = `Edit school: ${school.code}`;
      body.querySelector('[name="schCode"]').value = school.code;
      body.querySelector('[name="schName"]').value = school.name;
      body.querySelector('[name="schOwner"]').value = school.ownerEmail || '';
      body.querySelector('[name="schRecommender"]').value = school.recommenderCode || '';
      body.querySelector('[name="schEnabled"]').value = String(school.enabled);
      body.querySelector('#school-form-title').scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });

  // Marketplace-selling toggle — instant on/off per school, same pattern as
  // the Recent Users "Unlimited" checkbox (wireRecentUsersControls above).
  const marketplaceStatus = body.querySelector('#school-marketplace-status');
  body.querySelectorAll('.marketplace-selling-toggle').forEach((checkbox) => {
    checkbox.addEventListener('change', async () => {
      const row = checkbox.closest('.school-row');
      const code = row?.dataset.code;
      const enabled = checkbox.checked;
      checkbox.disabled = true;
      if (marketplaceStatus) {
        marketplaceStatus.className = 'admin-save-status';
        marketplaceStatus.textContent = `${enabled ? 'Enabling' : 'Disabling'} marketplace selling for ${code}...`;
      }
      try {
        await setSchoolMarketplaceSelling({ code, enabled });
        if (marketplaceStatus) {
          marketplaceStatus.className = 'admin-save-status is-ok';
          marketplaceStatus.textContent = `${code}: marketplace selling ${enabled ? 'enabled' : 'disabled'}.`;
        }
      } catch (err) {
        checkbox.checked = !enabled; // revert on failure
        if (marketplaceStatus) {
          marketplaceStatus.className = 'admin-save-status is-fail';
          marketplaceStatus.textContent = friendlyError(err, 'Could not update marketplace selling.');
        }
      } finally {
        checkbox.disabled = false;
      }
    });
  });

  // Per-school curated-label association — see functions/schools.js
  // setSchoolPinnedLabels. Provisions free school access for every listing
  // in the selected labels and seeds them as pinned for every CURRENT
  // student under that school; a student who joins later gets the same
  // seeding lazily at their next sign-in.
  body.querySelectorAll('.school-labels-save-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const code = btn.dataset.code;
      const select = body.querySelector(`.school-labels-select[data-code="${CSS.escape(code)}"]`);
      const labelIds = select ? Array.from(select.selectedOptions).map((o) => o.value) : [];
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = '…';
      try {
        const res = await setSchoolPinnedLabels({ code, labelIds });
        if (marketplaceStatus) {
          marketplaceStatus.className = 'admin-save-status is-ok';
          marketplaceStatus.textContent = `${code}: ${res.listingCount} listing${res.listingCount === 1 ? '' : 's'} across ${labelIds.length} label${labelIds.length === 1 ? '' : 's'} — ${res.grantedCount} newly granted, pinned for ${res.studentsSeeded} current student${res.studentsSeeded === 1 ? '' : 's'}.`;
        }
      } catch (err) {
        if (marketplaceStatus) {
          marketplaceStatus.className = 'admin-save-status is-fail';
          marketplaceStatus.textContent = friendlyError(err, 'Could not save label association.');
        }
      } finally {
        btn.disabled = false;
        btn.textContent = originalText;
      }
    });
  });

  // Recommender save + click-a-row-to-edit.
  const recommenderStatus = body.querySelector('#recommender-save-status');
  body.querySelector('#save-recommender-btn').addEventListener('click', async () => {
    recommenderStatus.className = 'admin-save-status';
    recommenderStatus.textContent = 'Saving…';
    try {
      const res = await upsertRecommender({
        code: body.querySelector('[name="recCode"]').value.trim() || null,
        name: body.querySelector('[name="recName"]').value.trim(),
        email: body.querySelector('[name="recEmail"]').value.trim() || null,
        phone: body.querySelector('[name="recPhone"]').value.trim() || null,
        commissionPct: Number(body.querySelector('[name="recCommission"]').value),
        durationYears: Number(body.querySelector('[name="recDuration"]').value),
        enabled: body.querySelector('[name="recEnabled"]').value === 'true',
      });
      recommenderStatus.className = 'admin-save-status is-ok';
      recommenderStatus.textContent = `Saved — code ${res.code}. Reload the dashboard to see the updated table.`;
    } catch (err) {
      recommenderStatus.className = 'admin-save-status is-fail';
      recommenderStatus.textContent = err.message || 'Could not save recommender.';
    }
  });

  body.querySelectorAll('.recommender-row').forEach((row) => {
    row.addEventListener('click', () => {
      const recommender = (c.recommenders || []).find((x) => x.code === row.dataset.code);
      if (!recommender) return;
      body.querySelector('#recommender-form-title').textContent = `Edit recommender: ${recommender.code}`;
      body.querySelector('[name="recCode"]').value = recommender.code;
      body.querySelector('[name="recName"]').value = recommender.name;
      body.querySelector('[name="recEmail"]').value = recommender.email || '';
      body.querySelector('[name="recPhone"]').value = recommender.phone || '';
      body.querySelector('[name="recCommission"]').value = recommender.commissionPct ?? '';
      body.querySelector('[name="recDuration"]').value = recommender.durationYears ?? '';
      body.querySelector('[name="recEnabled"]').value = String(recommender.enabled);
      body.querySelector('#recommender-form-title').scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });
}

// Sample invoice modal — enter a list price (and optionally a school code)
// and see exactly where every paisa goes, computed by the same server-side
// computeSplit as a real purchase.
// Admin drill-down: everything one user has created (every course/topic —
// i.e. every animation/simulation), with the per-topic API request count
// written by functions/entitlement.js recordContentUsage. Opened by
// clicking a row in the "All Registered Users" table.
function openUserContentModal(uid, name, email) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" style="width:min(94vw,760px);max-height:86vh;overflow:auto;">
      <h2 style="margin-bottom:2px;">${escapeHtml(name || uid)}</h2>
      <p style="color:var(--text-dim);margin-top:0;">${escapeHtml(email || uid)}</p>
      <div id="uc-body"><p style="color:var(--text-dim);">Loading content…</p></div>
      <div class="modal-actions"><button class="btn" id="uc-close">Close</button></div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#uc-close').addEventListener('click', () => overlay.remove());

  const ucBody = overlay.querySelector('#uc-body');
  getUserContent(uid).then(({ courses, totals }) => {
    if (!courses.length) {
      ucBody.innerHTML = '<p style="color:var(--text-dim);">This user hasn\'t created any courses or topics yet.</p>';
      return;
    }
    ucBody.innerHTML = `
      <p style="color:var(--text-dim);font-size:0.85rem;">${formatNumber(totals.courseCount)} courses · ${formatNumber(totals.topicCount)} topics (animations/simulations) · <strong>${formatNumber(totals.requestCount)}</strong> total API requests used.</p>
      ${courses.map((course) => `
        <div style="margin-top:14px;">
          <h4 style="margin:0 0 6px;">${escapeHtml(course.emoji || '📘')} ${escapeHtml(course.title)}${course.deletedAt ? ' <span style="color:var(--text-dim);font-weight:400;font-size:0.78rem;">(trashed)</span>' : ''}
            <span style="color:var(--text-dim);font-weight:400;font-size:0.8rem;"> — ${formatNumber(course.topicCount)} topics, ${formatNumber(course.requestCount)} requests</span>
          </h4>
          ${course.topics.length ? `
          <table class="admin-table">
            <thead><tr><th>Title</th><th>Type</th><th>Requests</th><th>Input tokens</th><th>Output tokens</th><th>Last request</th></tr></thead>
            <tbody>
              ${course.topics.map((t) => `
                <tr>
                  <td>${escapeHtml(t.title)}${t.deletedAt ? ' <span style="color:var(--text-dim);font-size:0.75rem;">(trashed)</span>' : ''}</td>
                  <td>${escapeHtml(t.type)}</td>
                  <td>${formatNumber(t.requestCount)}</td>
                  <td>${formatNumber(t.inputTokens)}</td>
                  <td>${formatNumber(t.outputTokens)}</td>
                  <td>${t.lastRequestAt ? new Date(t.lastRequestAt).toLocaleString() : '—'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>` : '<p style="color:var(--text-dim);font-size:0.82rem;">No topics in this course yet.</p>'}
        </div>
      `).join('')}
    `;
  }).catch((err) => {
    ucBody.innerHTML = `<p style="color:var(--danger,#c0392b);">${escapeHtml(friendlyError(err, 'Could not load this user\'s content.'))}</p>`;
  });
}

function openSampleInvoiceModal(c) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" style="width:min(92vw,560px);">
      <h2>🧾 Sample invoice</h2>
      <p style="color:var(--text-dim);margin-top:-8px;">Computed server-side with the live settings — identical math to a real purchase.</p>
      <div class="field-row">
        <div class="field"><label>Library list price (₹)</label><input type="number" id="si-price" value="100" min="0" /></div>
        <div class="field"><label>School seats (for seat-cost preview)</label><input type="number" id="si-seatqty" value="30" min="1" /></div>
      </div>
      <div class="admin-actions">
        <button class="btn btn-primary" id="si-run">Calculate</button>
        <span id="si-status" class="admin-save-status"></span>
      </div>
      <div id="si-result"></div>
      <div class="modal-actions"><button class="btn" id="si-close">Close</button></div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#si-close').addEventListener('click', () => overlay.remove());

  overlay.querySelector('#si-run').addEventListener('click', async () => {
    const status = overlay.querySelector('#si-status');
    const result = overlay.querySelector('#si-result');
    status.className = 'admin-save-status';
    status.textContent = 'Calculating…';
    result.innerHTML = '';
    try {
      const { split, seatPreview } = await previewInvoice({
        listRupees: Number(overlay.querySelector('#si-price').value),
        seatQty: Number(overlay.querySelector('#si-seatqty').value),
      });
      status.textContent = '';
      const line = (label, paise, opts = {}) => `
        <tr style="${opts.strong ? 'font-weight:800;' : ''}${opts.dim ? 'color:var(--text-dim);' : ''}">
          <td>${label}</td><td style="text-align:right;">${opts.neg ? '−' : ''}${formatPaise(Math.abs(paise))}</td>
        </tr>`;
      result.innerHTML = `
        <table class="admin-table" style="margin-top:8px;">
          <tbody>
            ${line('Library list price', split.listPaise, { strong: true })}
            ${line('Buyer pays (from wallet)', split.buyerPaysPaise, { strong: true })}
            ${line(`Seller earning (list − ${split.commissionPct}% commission)`, split.sellerEarningPaise)}
            ${line('… shown to the seller as "Processing fee"', split.processingFeePaise, { neg: true, dim: true })}
            ${line('Platform keeps', split.platformNetPaise, { strong: true })}
          </tbody>
        </table>
        <p style="color:var(--text-dim);font-size:0.82rem;margin-top:8px;">Library check: buyer ${formatPaise(split.buyerPaysPaise)} = seller ${formatPaise(split.sellerEarningPaise)} + platform ${formatPaise(split.platformNetPaise)}.</p>
        <table class="admin-table" style="margin-top:14px;">
          <tbody>
            ${line(`${seatPreview.qty} seat(s) × ₹${seatPreview.seatWholesaleRupees} (${seatPreview.seatTermDays}-day term)`, seatPreview.qty * seatPreview.seatWholesaleRupees * 100, { dim: true })}
            ${line('Minimum charge floor', (seatPreview.minSchoolChargeRupees || 0) * 100, { dim: true })}
            ${line('School pays (the higher of the two)', seatPreview.costPaise, { strong: true })}
          </tbody>
        </table>
        <p style="color:var(--text-dim);font-size:0.82rem;margin-top:8px;">That charge is the platform's school revenue per term — billed to the school wallet. What the school charges its students is off-platform.</p>
      `;
    } catch (err) {
      status.className = 'admin-save-status is-fail';
      status.textContent = err.message || 'Could not calculate.';
    }
  });
}

function settingsSignature(settings) {
  return JSON.stringify({
    defaultProvider: settings.defaultProvider,
    defaultModel: settings.defaultModel,
    defaultReasoningEffort: settings.defaultReasoningEffort || null,
    agentModelProvider: settings.agentModelProvider,
    agentModel: settings.agentModel,
    agentModelReasoningEffort: settings.agentModelReasoningEffort || null,
    verificationProvider: settings.verificationProvider,
    verificationModel: settings.verificationModel,
    verificationReasoningEffort: settings.verificationReasoningEffort || null,
    askAiProvider: settings.askAiProvider,
    askAiModel: settings.askAiModel,
    askAiReasoningEffort: settings.askAiReasoningEffort || null,
    imageProvider: settings.imageProvider,
    imageModel: settings.imageModel,
    imageQuality: settings.imageQuality,
    imageSize: settings.imageSize,
    imageRecognitionProvider: settings.imageRecognitionProvider,
    imageRecognitionClassifyModel: settings.imageRecognitionClassifyModel,
    imageRecognitionReplicaModel: settings.imageRecognitionReplicaModel,
  });
}

function normalizeCatalog(catalog) {
  return {
    openai: Array.isArray(catalog?.openai) && catalog.openai.length ? catalog.openai : FALLBACK_MODEL_CATALOG.openai,
    deepseek: Array.isArray(catalog?.deepseek) && catalog.deepseek.length ? catalog.deepseek : FALLBACK_MODEL_CATALOG.deepseek,
    anthropic: Array.isArray(catalog?.anthropic) && catalog.anthropic.length ? catalog.anthropic : FALLBACK_MODEL_CATALOG.anthropic,
    gemini: Array.isArray(catalog?.gemini) && catalog.gemini.length ? catalog.gemini : FALLBACK_MODEL_CATALOG.gemini,
  };
}

function normalizeImageCatalog(catalog) {
  return {
    openai: Array.isArray(catalog?.openai) && catalog.openai.length ? catalog.openai : FALLBACK_IMAGE_MODEL_CATALOG.openai,
    gemini: Array.isArray(catalog?.gemini) && catalog.gemini.length ? catalog.gemini : FALLBACK_IMAGE_MODEL_CATALOG.gemini,
  };
}

// "Is what I'm looking at a choice, or a default that happens to look the
// same?" The panel could not answer that, which is why a settings reset was
// suspected of being a deploy-time event for weeks. Stored choices live in
// Firestore and survive every deploy; anything not listed here is following
// the code default and will move if that default moves.
function settingsProvenanceLine(provenance) {
  if (!provenance) return '';
  const count = (provenance.explicitKeys || []).length;
  if (!count) {
    return `<p style="color:var(--text-dim);font-size:0.85rem;margin:6px 0 0;">
      No settings have been saved yet — every value below is a built-in default.</p>`;
  }
  const when = provenance.updatedAt ? new Date(provenance.updatedAt).toLocaleString() : 'unknown time';
  const who = provenance.updatedBy ? ` by ${escapeHtml(provenance.updatedBy)}` : '';
  return `<p style="color:var(--text-dim);font-size:0.85rem;margin:6px 0 0;">
    ${count} setting${count === 1 ? '' : 's'} saved${who} on ${escapeHtml(when)} — saved values are stored and survive deployments.
    Anything not saved follows the built-in default.</p>`;
}

function modelOptionsFor(catalog, provider) {
  return (catalog[provider] || []).map((m) => ({ value: m.id, label: m.label || m.id }));
}

function reasoningOptionsFor(catalog, provider, modelId) {
  const model = (catalog[provider] || []).find((m) => m.id === modelId) || (catalog[provider] || [])[0];
  const levels = model?.reasoningLevels?.length ? model.reasoningLevels : [''];
  return levels.map((level) => ({ value: level, label: level || 'none' }));
}


// Content search embedding backfill (see functions/embeddingBackfill.js).
// runEmbeddingBackfill does ONE bounded batch per call and returns
// immediately — this loop just calls it again until status is 'done',
// a poll-driven "start/poll" shape like the (removed) book-import flow,
// collapsed into a single repeatedly-called function instead of a separate
// start/poll pair.
function wireEmbeddingBackfillControls(root) {
  const startBtn = root.querySelector('#embedding-backfill-start');
  const statusEl = root.querySelector('#embedding-backfill-status');

  function describe(result) {
    const t = result.topics;
    const l = result.listings;
    return `Topics: ${t.processed} embedded, ${t.skipped} skipped, ${t.errors} failed${t.done ? ' (done)' : '…'}. `
      + `Listings: ${l.processed} embedded, ${l.skipped} skipped, ${l.errors} failed${l.done ? ' (done)' : '…'}.`;
  }

  startBtn.addEventListener('click', async () => {
    startBtn.disabled = true;
    statusEl.textContent = 'Starting…';
    try {
      let result = await runEmbeddingBackfill();
      statusEl.textContent = describe(result);
      while (result.status !== 'done') {
        result = await runEmbeddingBackfill();
        statusEl.textContent = describe(result);
      }
      statusEl.textContent = `Done. ${describe(result)}`;
    } catch (err) {
      statusEl.textContent = friendlyError(err, 'Backfill failed.');
    } finally {
      startBtn.disabled = false;
    }
  });

  getEmbeddingBackfillStatus().then((result) => {
    if (result.status && result.status !== 'not_started') statusEl.textContent = describe(result);
  }).catch(() => {});
}

// Topic ownerUid backfill (see functions/topicOwnerBackfill.js) — same
// bounded-batch-per-call, poll-until-done shape as the embedding backfill
// above.
function wireTopicOwnerBackfillControls(root) {
  const startBtn = root.querySelector('#topic-owner-backfill-start');
  const statusEl = root.querySelector('#topic-owner-backfill-status');

  function describe(result) {
    return `${result.processed} tagged, ${result.skipped} already tagged, ${result.errors} failed`
      + `${result.status === 'done' ? ' (done)' : ` (user ${result.userIndex}/${result.totalUsers})…`}`;
  }

  startBtn.addEventListener('click', async () => {
    startBtn.disabled = true;
    statusEl.textContent = 'Starting…';
    try {
      let result = await runTopicOwnerBackfill();
      statusEl.textContent = describe(result);
      while (result.status !== 'done') {
        result = await runTopicOwnerBackfill();
        statusEl.textContent = describe(result);
      }
      statusEl.textContent = `Done. ${describe(result)}`;
    } catch (err) {
      statusEl.textContent = friendlyError(err, 'Backfill failed.');
    } finally {
      startBtn.disabled = false;
    }
  });

  getTopicOwnerBackfillStatus().then((result) => {
    if (result.status && result.status !== 'not_started') statusEl.textContent = describe(result);
  }).catch(() => {});
}

// modelFieldName defaults to the regular `${prefix}Model` convention every
// other slot follows (verificationModel, askAiModel, ...) — but the
// coding-agent slot's actual field is named `agentModel`, not
// `agentModelModel` (generationSettingsValidation.js's
// DEFAULT_GENERATION_SETTINGS deliberately avoided the doubled "Model"),
// so its caller passes the real name explicitly rather than this silently
// looking for a field that was never on the page.
function wireProviderModelControls(root, catalog, prefix, modelFieldName = `${prefix}Model`) {
  const provider = root.querySelector(`[name="${prefix}Provider"]`);
  const model = root.querySelector(`[name="${modelFieldName}"]`);
  const reasoning = root.querySelector(`[name="${prefix}ReasoningEffort"]`);

  provider.addEventListener('change', () => {
    const models = modelOptionsFor(catalog, provider.value);
    fillSelect(model, models, models[0]?.value || '');
    const reasonOptions = reasoningOptionsFor(catalog, provider.value, model.value);
    fillSelect(reasoning, reasonOptions, reasonOptions[0]?.value || '');
  });

  model.addEventListener('change', () => {
    const reasonOptions = reasoningOptionsFor(catalog, provider.value, model.value);
    fillSelect(reasoning, reasonOptions, reasonOptions[0]?.value || '');
  });
}

function wireImageProviderControl(root, imageCatalog) {
  const provider = root.querySelector('[name="imageProvider"]');
  const model = root.querySelector('[name="imageModel"]');
  provider.addEventListener('change', () => {
    const models = modelOptionsFor(imageCatalog, provider.value);
    fillSelect(model, models, models[0]?.value || '');
  });
}

// Image Recognition's provider select re-populates BOTH model dropdowns
// (classify + replica-build) from the general text/vision catalog — unlike
// wireImageProviderControl above, which only ever has one model field.
function wireImageRecognitionProviderControl(root, catalog) {
  const provider = root.querySelector('[name="imageRecognitionProvider"]');
  const classifyModel = root.querySelector('[name="imageRecognitionClassifyModel"]');
  const replicaModel = root.querySelector('[name="imageRecognitionReplicaModel"]');
  provider.addEventListener('change', () => {
    const models = modelOptionsFor(catalog, provider.value);
    fillSelect(classifyModel, models, models[0]?.value || '');
    fillSelect(replicaModel, models, models[0]?.value || '');
  });
}

// See wireProviderModelControls' comment above for why modelFieldName can't
// always just be derived from prefix.
function setProviderGroupValues(root, catalog, prefix, providerValue, modelValue, reasoningValue, modelFieldName = `${prefix}Model`) {
  const provider = root.querySelector(`[name="${prefix}Provider"]`);
  const model = root.querySelector(`[name="${modelFieldName}"]`);
  const reasoning = root.querySelector(`[name="${prefix}ReasoningEffort"]`);
  provider.value = providerValue;
  fillSelect(model, modelOptionsFor(catalog, providerValue), modelValue);
  fillSelect(reasoning, reasoningOptionsFor(catalog, providerValue, model.value), reasoningValue || '');
}

// Repopulates a <select>, KEEPING the saved value even when the fresh
// option list doesn't contain it.
//
// Previously the value was simply dropped when it wasn't in the list, and
// the browser fell back to option 0 with nothing on screen to say so. The
// model lists come from a live /v1/models call (see functions/admin.js
// fetchProviderModels), so an unset key, an outage or a rate limit empties
// the OpenAI list — and then the form quietly submitted a different model
// than the one that was saved. A settings page that changes your settings
// while you look at it is worse than one that fails loudly.
//
// The saved value is re-added, marked, and kept selected, so the admin sees
// exactly what is in force and can tell the catalogue didn't load.
function fillSelect(select, options, value) {
  const list = options.slice();
  const wanted = String(value ?? '');
  if (wanted && !list.some((opt) => opt.value === wanted)) {
    list.unshift({ value: wanted, label: `${wanted} (saved — not in catalogue)` });
  }
  select.innerHTML = list.map((opt) => `<option value="${escapeHtml(opt.value)}">${escapeHtml(opt.label)}</option>`).join('');
  if (list.some((opt) => opt.value === wanted)) select.value = wanted;
}

function selectField(label, name, options, value) {
  return `
    <label class="field">
      <span>${escapeHtml(label)}</span>
      <select name="${name}">
        ${options.map((opt) => {
          const option = typeof opt === 'string' ? { value: opt, label: opt || 'none' } : opt;
          return `<option value="${escapeHtml(option.value)}" ${String(value || '') === option.value ? 'selected' : ''}>${escapeHtml(option.label)}</option>`;
        }).join('')}
      </select>
    </label>
  `;
}

function statCard(label, value) {
  return `
    <div class="admin-stat">
      <span>${escapeHtml(label)}</span>
      <strong>${formatNumber(value)}</strong>
    </div>
  `;
}

function slotLabel(slot) {
  if (slot === 'image') return 'Image generation model';
  if (slot === 'agentModel') return 'Coding agent — main loop model';
  if (slot === 'default') return 'Standard content model';
  if (slot === 'verification') return 'Verification model';
  if (slot === 'askAi') return 'Dynamic content (Ask AI) model';
  if (slot === 'imageRecognitionClassify') return 'Image recognition — classification model';
  if (slot === 'imageRecognitionReplica') return 'Image recognition — replica-build model';
  return 'Model';
}

function renderCheckResults(container, checks) {
  if (!checks || !checks.length) {
    container.innerHTML = '';
    return;
  }
  container.innerHTML = checks.map((c) => {
    const ok = c.ok !== false;
    const detail = ok ? 'Working' : shortError(c.error);
    const model = `${c.provider || '?'} / ${c.model || '?'}`;
    return `
      <div class="model-check-row ${ok ? 'ok' : 'fail'}">
        <span class="mc-icon">${ok ? '✓' : '✕'}</span>
        <span class="mc-slot">${escapeHtml(slotLabel(c.slot))}</span>
        <span class="mc-model">${escapeHtml(model)}</span>
        <span class="mc-detail">${escapeHtml(detail)}</span>
      </div>
    `;
  }).join('');
}

function shortError(msg) {
  const s = String(msg || 'Not working').trim();
  return s.length > 180 ? `${s.slice(0, 177)}…` : s;
}

function readSettings(root) {
  return {
    defaultProvider: root.querySelector('[name="defaultProvider"]').value,
    defaultModel: root.querySelector('[name="defaultModel"]').value.trim(),
    defaultReasoningEffort: root.querySelector('[name="defaultReasoningEffort"]').value || null,
    agentModelProvider: root.querySelector('[name="agentModelProvider"]').value,
    agentModel: root.querySelector('[name="agentModel"]').value.trim(),
    agentModelReasoningEffort: root.querySelector('[name="agentModelReasoningEffort"]').value || null,
    verificationProvider: root.querySelector('[name="verificationProvider"]').value,
    verificationModel: root.querySelector('[name="verificationModel"]').value.trim(),
    verificationReasoningEffort: root.querySelector('[name="verificationReasoningEffort"]').value || null,
    askAiProvider: root.querySelector('[name="askAiProvider"]').value,
    askAiModel: root.querySelector('[name="askAiModel"]').value.trim(),
    askAiReasoningEffort: root.querySelector('[name="askAiReasoningEffort"]').value || null,
    imageProvider: root.querySelector('[name="imageProvider"]').value,
    imageModel: root.querySelector('[name="imageModel"]').value.trim(),
    imageQuality: root.querySelector('[name="imageQuality"]').value,
    imageSize: root.querySelector('[name="imageSize"]').value,
    imageRecognitionProvider: root.querySelector('[name="imageRecognitionProvider"]').value,
    imageRecognitionClassifyModel: root.querySelector('[name="imageRecognitionClassifyModel"]').value.trim(),
    imageRecognitionReplicaModel: root.querySelector('[name="imageRecognitionReplicaModel"]').value.trim(),
  };
}

function formatNumber(n) {
  return Number(n || 0).toLocaleString();
}

function formatDuration(seconds) {
  const s = Number(seconds || 0);
  if (s <= 0) return '—';
  if (s < 90) return `${Math.round(s)}s`;
  const minutes = Math.floor(s / 60);
  const remainder = Math.round(s % 60);
  return `${minutes}m ${remainder}s`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
